(ns ui.wallet.on-demand
  "The wallet panels for the on-demand model: the customer buys credits
   with one-time charges of an amount of their choice, the $0 subscription
   only keeps the payment method on file, and the Auto Top-Up adds a chosen
   amount whenever the balance drops below the fixed threshold.

   The checkout hands over to `control.wallet-checkout`
   (slices/wallet/control): fully working in the sandbox, and a clean
   'not available yet' page in production until the $0 price is created
   there. Subscribers get \"Add credits\" through `:command/wallet-charge`
   and the Auto Top-Up settings through
   `:command/subscription-auto-refill-config`. Monthly and yearly
   subscriptions keep their panels in `ui.wallet.plan`.

   Texts are `[:i18n ...]` references, resolved by `ui.wallet.page`."
  (:require [lambdaisland.uri :as uri]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [storrito.ui.button :as button]
            [storrito.wallet :as wallet]
            [ui.elements]
            [ui.subscription.core :as core]
            [ui.wallet.customer-conflict :as customer-conflict]
            [ui.wallet.i18n :as i18n]
            [ui.wallet.top-up :as top-up]))

(defn auto-top-up?
  "The Auto Top-Up choice on this page, `default` while the customer has
   not touched the checkbox. Unticked buys the credits one-time only."
  [w default]
  (let [chosen (:wallet/auto-top-up? w)]
    (if (nil? chosen)
      default
      (boolean chosen))))

(def checkout-auto-top-up-default
  "Unticked for the first purchase: a signup who was promised \"from $5\"
   should not find a pre-ticked recurring charge next to the button. The
   hint under the box explains what they gain by ticking it."
  false)

(def add-credits-auto-top-up-default
  "Unticked as well: the box is the only Auto Top-Up control a subscriber
   sees while no choice is stored, and an unticked box is what the server
   does (`app.wallet.auto-top-up/check!` is a no-op without the stored
   flag). It used to be pre-ticked and only stored with the purchase, so a
   $97 monthly customer saw a ticked box, bought nothing, ran below $0 and
   no automatic charge ever happened. Ticking it now stores the choice
   right away (`persist-choice-actions`)."
  false)

(defn amount-error
  [w]
  (let [amount (top-up/amount w)]
    (cond
      (nil? amount)
      [:i18n :top-up/amount-missing]

      (< amount wallet/minimum-amount)
      [:i18n :top-up/amount-too-small {:minimum wallet/minimum-amount}]

      (> amount wallet/maximum-amount)
      [:i18n :top-up/amount-too-large {:maximum wallet/maximum-amount}])))

(defn chooser
  [w]
  [:div.mt-4.flex.flex-wrap.items-center.gap-2
   (map (fn [preset]
          (top-up/chip w preset))
        top-up/presets)
   (top-up/custom-input w)])

(defn config-command
  "`:command/wallet-auto-top-up-config` (app.wallet.config): stores the
   flag and the amount."
  [enabled? amount]
  {:command/kind :command/wallet-auto-top-up-config
   :command/data {:enabled? enabled?
                  :amount amount}})

(defn rule
  [amount]
  [:i18n :on-demand/rule {:threshold wallet/on-demand-threshold
                          :amount amount}])

(defn checkout-url
  "The on-demand checkout on the control-server. With Auto Top-Up ticked the
   amount and the threshold travel along, so the checkout page describes the
   automatic charges instead of promising \"Nothing recurring\"."
  [w]
  (-> (:checkout-url (query/get-result w core/subscription-query))
      (uri/uri)
      (uri/assoc-query :on-demand "true"
                       :quantity (top-up/amount w))
      (cond-> (auto-top-up? w checkout-auto-top-up-default)
        (uri/assoc-query :auto-top-up (top-up/amount w)
                         :auto-top-up-threshold wallet/on-demand-threshold))
      (uri/uri-str)))

(defn checkout-actions
  "Logs the click as a funnel step, persists the Auto Top-Up choice first
   when it was ticked, claims the welcome campaign like the old calculator
   did, then opens the checkout. Without a Paddle customer the checkout
   cannot work, so the support modal opens instead
   (`ui.wallet.customer-conflict`)."
  [w]
  (let [auto-top-up (auto-top-up? w checkout-auto-top-up-default)
        log [:client-log/log :wallet/checkout-clicked
             {:wallet/amount (top-up/amount w)
              :wallet/auto-top-up (if auto-top-up "on" "off")}]
        redirect [[:data/command
                   top-up/claim-campaign-command
                   {:on-success [[:js/location (checkout-url w)]]}]]]
    (cond
      (customer-conflict/conflict? w)
      [log
       [:ui/modal-open customer-conflict/modal-id {}]]

      auto-top-up
      [log
       [:data/command
        (config-command true (top-up/amount w))
        {:on-success redirect}]]

      :else
      (into [log] redirect))))

(defn remember-choice-actions
  "The checkout's checkbox only remembers the choice on the page; it is
   stored with the checkout click (`checkout-actions`), as there is no
   subscription to charge before."
  [_w ticked?]
  [[:store/assoc :wallet/auto-top-up? ticked?]])

(defn persist-choice-actions
  "A subscriber's checkbox stores the choice the moment it is ticked, with
   the amount chosen above, so Auto Top-Up works without a purchase. The
   refreshed query then shows the settings panel instead of the box."
  [w ticked?]
  [[:store/assoc :wallet/auto-top-up? ticked?]
   [:data/command
    (config-command ticked? (top-up/amount w))
    {:on-success core/query-actions}]])

(defn auto-top-up-checkbox
  "Sits between the amount and the button with the rule spelled out and
   a hint what Auto Top-Up is good for; unticked the purchase is a plain
   one-time charge. `default` is the state before the customer touches
   the box (see `checkout-auto-top-up-default`), `on-change` yields the
   actions for the new state, `disabled?` greys the box out while the
   choice cannot be stored."
  [w {:keys [default on-change disabled?]}]
  (let [checked? (auto-top-up? w default)]
    [:div.mt-4
     [:label.flex.items-start.gap-3.cursor-pointer
      [:input {:type "checkbox"
               :checked checked?
               :disabled (boolean disabled?)
               ;; Centered in the first 20px text line, so a wrapped label
               ;; still starts next to the box:
               :class ["mt-0.5"]
               :on {:change (on-change w (not checked?))}}]
      [:span.text-sm.text-gray-700
       [:span.font-medium.text-gray-900 [:i18n :on-demand/auto-top-up] ": "]
       (rule (or (top-up/amount w) "…"))]]
     [:p.mt-1.ml-7.text-xs.text-gray-500
      [:i18n :on-demand/auto-top-up-hint]]]))

(defn checkout-button
  [w]
  (let [config (config-command true (top-up/amount w))
        issued? (or (command/issued? w top-up/claim-campaign-command)
                    (command/issued? w config))
        error (amount-error w)]
    (list
      [:button
       {:class (concat button/button-primary-css
                       ["w-full" "text-base" "mt-4"])
        :disabled (boolean (or issued?
                               error))
        :on {:click (checkout-actions w)}}
       (cond
         issued? [:i18n :top-up/opening-checkout]
         error [:i18n :on-demand/add-credits]
         :else [:i18n :on-demand/add {:amount (top-up/amount w)}])]
      (when error
        [:p.mt-2.text-sm.text-red-500 error])
      (when-let [error* (core/command-error w config [:i18n :page/command-failed])]
        [:p.mt-2.text-sm.text-red-500 error*]))))

(defn campaign-note
  "The eligible campaign (the welcome discount) spelled out next to the
   amount: it lowers the daily rate of the service, it is not a coupon for
   the top-up. Two customers expected 50% off their $20 (brain-43); the
   Paddle checkout cannot show a rate discount."
  [campaign]
  (when-let [feature (:campaign/feature campaign)]
    [:p.mt-3.text-sm.text-green-700
     [:span.font-medium (:campaign/name campaign) ": "]
     [:i18n :on-demand/discount-off {:percent (int (* 100 (:campaign/discount campaign)))}]
     " "
     [:i18n (i18n/service-key feature :name) {:fallback (name feature)}]
     ". "
     [:i18n :on-demand/discount-applied]]))

(defn checkout-panel
  "For customers without a subscription: the Paddle checkout creates the $0
   subscription and charges the chosen credits in the same transaction."
  [w]
  [:div {:class ui.elements/panel}
   [:h2 {:class ui.elements/h3} [:i18n :on-demand/add-credits]]
   [:p {:class (concat ui.elements/p-sm ["mt-1"])}
    [:i18n :on-demand/choose-amount]]
   (campaign-note (:campaign (query/get-result w core/subscription-query)))
   (chooser w)
   (auto-top-up-checkbox w {:default checkout-auto-top-up-default
                            :on-change remember-choice-actions})
   (checkout-button w)
   [:p.mt-3.text-sm.text-gray-600
    [:i18n :on-demand/charged-once]]])

(defn charge-command
  "The wallet's own one-time charge (see app.wallet.charge): no optimistic
   crediting, no billing-date re-anchoring."
  [amount]
  {:command/kind :command/wallet-charge
   :command/data {:amount amount}})

(defn auto-top-up-undecided?
  "True while the organization never made an Auto Top-Up choice: the flag
   is absent, not false (disabling stores an explicit false). Buying
   credits then shows the pre-checked checkbox — the upgrade moment for
   existing monthly and yearly customers, who can untick it."
  [subscription]
  (nil? (:organization/auto-refill-enabled subscription)))

(defn add-credits-actions
  "The one-time charge, preceded by storing the Auto Top-Up choice while
   the checkbox is shown (mirrors `checkout-actions`)."
  [w subscription amount]
  (let [charge [[:js/confirm [:i18n :on-demand/confirm-charge {:amount amount}]]
                [:data/command
                 (charge-command amount)
                 {:on-success (concat core/query-actions
                                      [core/repeat-import-and-refresh])}]]]
    (if (auto-top-up-undecided? subscription)
      [(first charge)
       [:data/command
        (config-command (auto-top-up? w add-credits-auto-top-up-default) amount)
        {:on-success [(second charge)]}]]
      charge)))

(defn add-credits-panel
  [w]
  (let [subscription (query/get-result w core/subscription-query)
        amount (top-up/amount w)
        cmd (charge-command amount)
        issued? (command/issued? w cmd)
        error (amount-error w)]
    [:div {:class ui.elements/panel}
     [:h2 {:class ui.elements/h3} [:i18n :on-demand/add-credits]]
     [:p {:class (concat ui.elements/p-sm ["mt-1"])}
      [:i18n :on-demand/charged-to-card]]
     (chooser w)
     (when (auto-top-up-undecided? subscription)
       (auto-top-up-checkbox w {:default add-credits-auto-top-up-default
                                :on-change persist-choice-actions
                                ;; The stored amount is the one chosen
                                ;; above, so an invalid amount cannot be
                                ;; stored either:
                                :disabled? (or error
                                               (command/issued? w (config-command true amount))
                                               (command/issued? w (config-command false amount)))}))
     [:button
      {:class (concat button/button-primary-css
                      ["w-full" "text-base" "mt-4"])
       :disabled (boolean (or issued?
                              error))
       :on {:click (add-credits-actions w subscription amount)}}
      (cond
        issued? [:i18n :on-demand/charging]
        error [:i18n :on-demand/add-credits]
        :else [:i18n :on-demand/add-now {:amount amount}])]
     (when error
       [:p.mt-2.text-sm.text-red-500 error])
     (when-let [error* (or (core/command-error w cmd [:i18n :page/command-failed])
                           (core/command-error w (config-command true amount) [:i18n :page/command-failed]))]
       [:p.mt-2.text-sm.text-red-500 error*])
     (when (command/success? w cmd)
       [:p.mt-2.text-sm.text-green-600
        [:i18n :on-demand/payment-accepted]])
     [:p.mt-3.text-xs.text-gray-500 [:i18n :on-demand/prices-excl-vat]]]))

(defn stored-auto-top-up-amount
  [w subscription]
  (or (:organization/auto-refill-amount subscription)
      (top-up/amount w)
      20))

(defn chosen-auto-top-up-amount
  "The amount in the editor: what the customer picked on this page, else
   the stored one. nil while the custom field holds no positive number."
  [w subscription]
  (let [chosen (:wallet/auto-top-up-amount w)]
    (cond
      (nil? chosen)
      (stored-auto-top-up-amount w subscription)

      (and (number? chosen)
           (pos? chosen))
      chosen

      :else
      nil)))

(defn auto-top-up-amount-error
  [amount]
  (cond
    (nil? amount)
    [:i18n :top-up/amount-missing]

    (< amount wallet/minimum-amount)
    [:i18n :top-up/amount-too-small {:minimum wallet/minimum-amount}]

    (> amount wallet/maximum-amount)
    [:i18n :on-demand/amount-too-large {:maximum wallet/maximum-amount}]))

(defn auto-top-up-chip
  [amount selected]
  [:button
   {:type "button"
    :class (concat ["rounded-md"]
                   top-up/choice-css
                   (if (= amount selected)
                     top-up/choice-selected-css
                     top-up/choice-unselected-css))
    :on {:click [[:store/assoc :wallet/auto-top-up-amount amount]]}}
   (str "$" amount)])

(defn auto-top-up-input
  "Shows the raw store value so the field can be cleared while typing."
  [w subscription]
  (let [chosen (:wallet/auto-top-up-amount w)]
    [:div.relative
     [:span.absolute.inset-y-0.left-3.flex.items-center.text-gray-500 "$"]
     [:input
      {:type "number"
       :min wallet/minimum-amount
       :step 1
       :aria-label [:i18n :on-demand/custom-auto-top-up-amount]
       :class ["w-28" "rounded-md" "border-0" "py-2" "pl-7" "pr-3" "text-sm"
               "text-gray-900" "ring-1" "ring-inset" "ring-gray-300"
               "focus:ring-2" "focus:ring-inset" "focus:ring-secondary-500"]
       :value (cond
                (nil? chosen) (stored-auto-top-up-amount w subscription)
                (js/isNaN chosen) ""
                :else chosen)
       :on {:input [[:store/assoc :wallet/auto-top-up-amount :event/target.value-int]]}}]]))

(defn auto-top-up-editor
  "Chips and a custom field pick the amount, an explicit Save stores it.
   The Save button only appears while the choice differs from the stored
   amount, and the choice is dropped again once saved."
  [w subscription]
  (let [stored (stored-auto-top-up-amount w subscription)
        chosen (chosen-auto-top-up-amount w subscription)
        error (auto-top-up-amount-error chosen)
        save (config-command true chosen)
        issued? (command/issued? w save)
        dirty? (and (some? (:wallet/auto-top-up-amount w))
                    (not= chosen stored))]
    (list
      [:div.mt-4.flex.flex-wrap.items-center.gap-2
       (map (fn [preset]
              (auto-top-up-chip preset chosen))
            top-up/presets)
       (auto-top-up-input w subscription)
       (when dirty?
         [:button
          {:class (concat button/button-primary-css ["ml-2"])
           :disabled (boolean (or issued?
                                  error))
           :on {:click [[:data/command
                         save
                         {:on-success (conj core/query-actions
                                            [:store/dissoc :wallet/auto-top-up-amount])}]]}}
          (if issued?
            [:i18n :on-demand/saving]
            [:i18n :on-demand/save])])]
      (when (and dirty?
                 error)
        [:p.mt-2.text-sm.text-red-500 error])
      (when-let [error* (core/command-error w save [:i18n :page/command-failed])]
        [:p.mt-2.text-sm.text-red-500 error*]))))

(defn auto-top-up-panel
  "Hidden while a cancellation is scheduled: the customer is about to leave
   and should not opt into future automatic charges."
  [w]
  (let [subscription (query/get-result w core/subscription-query)
        paddle-subscription (:organization/paddle-subscription subscription)
        enabled? (boolean (:organization/auto-refill-enabled subscription))
        toggle (config-command (not enabled?)
                               (stored-auto-top-up-amount w subscription))
        issued? (command/issued? w toggle)]
    (when-not (:scheduled_change paddle-subscription)
      [:div {:class ui.elements/panel}
       [:div.flex.items-center.justify-between
        [:h2 {:class ui.elements/h3} [:i18n :on-demand/auto-top-up]]
        [:label.flex.items-center.gap-2.cursor-pointer
         [:input {:type "checkbox"
                  :checked enabled?
                  :disabled issued?
                  :on {:change [[:data/command toggle {:on-success core/query-actions}]]}}]
         [:span.text-sm.font-medium.text-gray-900
          (if enabled?
            [:i18n :on-demand/on]
            [:i18n :on-demand/off])]]]
       [:p {:class (concat ui.elements/p-sm ["mt-1"])}
        (rule (or (chosen-auto-top-up-amount w subscription) "…"))]
       (when enabled?
         (auto-top-up-editor w subscription))
       (when-let [error (core/command-error w toggle [:i18n :page/command-failed])]
         [:p.mt-2.text-sm.text-red-500 error])
       [:p.mt-4.text-xs.text-gray-500
        [:i18n :on-demand/one-charge-per-day]]
       ;; A monthly or yearly subscription is canceled in the plan strip
       ;; above; only the $0 subscription is "just a saved payment method":
       (when (wallet/on-demand? paddle-subscription)
         [:button
          {:class ["mt-3" "text-sm" "text-red-600" "hover:underline"]
           :on {:click [[:ui/modal-open :modals/subscription-cancel-modal {:on-demand? true}]]}}
          [:i18n :on-demand/remove-payment-method]])])))

(defn panels
  [w]
  (let [subscription (query/get-result w core/subscription-query)]
    (list
      (add-credits-panel w)
      ;; While no Auto Top-Up choice was ever made, the checkbox in the
      ;; Add credits panel is the single control; the settings panel
      ;; would show the same rule again with an inverted Off toggle. It
      ;; appears once ticking the box or buying stored a choice.
      (when-not (auto-top-up-undecided? subscription)
        (auto-top-up-panel w)))))
